import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CurrencyInput from '@/components/base/CurrencyInput';
import { formatTRY } from '@/lib/currencyFormat';
import {
  FOLLOW_UP_STATUSES,
  STATUSES_REQUIRING_FOLLOW_UP,
  getFollowUpStatusDef,
} from '@/lib/promiseStatus';

interface PaymentPromiseCardProps {
  promise: any;
  customer: any;
  reference: string | null;
  customerDebt: number;
  sourceRemaining?: number | null;
  isLoading: boolean;
  activities: any[];
  onEdit: () => void;
  onView: () => void;
  onTakePayment: () => void;
  onFollowUpStatusChange: (payload: {
    follow_up_status: string;
    next_follow_up_date?: string | null;
    next_follow_up_time?: string | null;
    last_contact_note?: string;
    contacted_by?: string;
  }) => Promise<void>;
  onPostpone: (newDate: string, newAmount: number) => void;
  onAddActivity: (note: string) => void;
  onDelete: () => void;
  onNavigate: (path: string) => void;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  direction: 'down' | 'up';
}

export default function PaymentPromiseCard({
  promise, customer, reference, customerDebt, sourceRemaining, isLoading,
  activities, onEdit, onView, onTakePayment, onFollowUpStatusChange,
  onPostpone, onAddActivity, onDelete, onNavigate,
}: PaymentPromiseCardProps) {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition>({ top: 0, left: 0, width: 200, direction: 'down' });

  const [showPostponeDialog, setShowPostponeDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [postponeDate, setPostponeDate] = useState(promise.promised_date);
  const [postponeAmount, setPostponeAmount] = useState(promise.promised_amount);
  const [activityNote, setActivityNote] = useState('');

  // Status change state
  const [statusNote, setStatusNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [statusChanging, setStatusChanging] = useState(false);
  const [pendingFollowUpStatus, setPendingFollowUpStatus] = useState<string | null>(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);

  const desktopTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
  const isOverdue = promise.promised_date < todayStr &&
    !['Ödeme Yaptı', 'İptal Edildi', 'Kısmi Ödeme Yaptı'].includes(promise.follow_up_status || 'Bekliyor');
  const isCompleted = promise.follow_up_status === 'Ödeme Yaptı' || promise.follow_up_status === 'Kısmi Ödeme Yaptı';

  const followUpStatus = promise.follow_up_status || 'Bekliyor';
  const statusDef = getFollowUpStatusDef(followUpStatus);

  const originalDateDisplay = promise.original_promise_date
    ? new Date(promise.original_promise_date).toLocaleDateString('tr-TR')
    : (promise.promised_date ? new Date(promise.promised_date).toLocaleDateString('tr-TR') : '—');

  const followUpDateDisplay = promise.next_follow_up_date
    ? new Date(promise.next_follow_up_date).toLocaleDateString('tr-TR')
    : '—';

  const followUpTimeDisplay = promise.next_follow_up_time || '—';

  const formattedLastContacted = promise.last_contacted_at
    ? new Date(promise.last_contacted_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  const calcDropdownPosition = useCallback((triggerEl: HTMLElement | null) => {
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    const dropdownHeight = 320; // max-h
    const menuWidth = Math.max(200, rect.width);
    const gap = 4;

    let top: number;
    let direction: 'down' | 'up';

    // Check if below trigger fits in viewport
    if (rect.bottom + dropdownHeight + gap <= window.innerHeight) {
      top = rect.bottom + gap;
      direction = 'down';
    } else if (rect.top - dropdownHeight - gap >= 0) {
      top = rect.top - dropdownHeight - gap;
      direction = 'up';
    } else {
      // Neither fully fits - prefer below but cap at viewport bottom
      top = rect.bottom + gap;
      direction = 'down';
    }

    // Clamp left so it doesn't overflow horizontally
    let left = rect.left;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8;
    }
    if (left < 8) left = 8;

    setDropdownPos({ top, left, width: menuWidth, direction });
  }, []);

  const handleDesktopToggle = useCallback(() => {
    if (isLoading || statusChanging) return;
    const willShow = !showStatusDropdown;
    setShowStatusDropdown(willShow);
    if (willShow) {
      // Small delay so DOM renders then measure
      setTimeout(() => calcDropdownPosition(desktopTriggerRef.current), 0);
    }
  }, [isLoading, statusChanging, showStatusDropdown, calcDropdownPosition]);

  const handleMobileToggle = useCallback(() => {
    if (isLoading || statusChanging) return;
    const willShow = !showMobileDropdown;
    setShowMobileDropdown(willShow);
    if (willShow) {
      setTimeout(() => calcDropdownPosition(mobileTriggerRef.current), 0);
    }
  }, [isLoading, statusChanging, showMobileDropdown, calcDropdownPosition]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showStatusDropdown && !showMobileDropdown) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const hitDropdown = dropdownRef.current?.contains(target);
      const hitDesktopTrigger = desktopTriggerRef.current?.contains(target);
      const hitMobileTrigger = mobileTriggerRef.current?.contains(target);
      if (!hitDropdown && !hitDesktopTrigger && !hitMobileTrigger) {
        setShowStatusDropdown(false);
        setShowMobileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStatusDropdown, showMobileDropdown]);

  // Reposition on scroll/resize while dropdown is open
  useEffect(() => {
    if (!showStatusDropdown && !showMobileDropdown) return;
    const handler = () => {
      const trigger = showStatusDropdown ? desktopTriggerRef.current : mobileTriggerRef.current;
      calcDropdownPosition(trigger);
    };
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [showStatusDropdown, showMobileDropdown, calcDropdownPosition]);

  const needsFollowUpFields = (status: string) => STATUSES_REQUIRING_FOLLOW_UP.includes(status);

  const closeDropdowns = () => {
    setShowStatusDropdown(false);
    setShowMobileDropdown(false);
  };

  const handleQuickStatusChange = async (newStatus: string) => {
    closeDropdowns();

    if (needsFollowUpFields(newStatus)) {
      setFollowUpDate(promise.next_follow_up_date || '');
      setFollowUpTime(promise.next_follow_up_time || '');
      setStatusNote('');
      setPendingFollowUpStatus(newStatus);
      setShowFollowUpModal(true);
      return;
    }

    await executeStatusChange(newStatus, null, null, '');
  };

  const executeStatusChange = async (newStatus: string, date: string | null, time: string | null, note: string) => {
    setStatusChanging(true);
    try {
      await onFollowUpStatusChange({
        follow_up_status: newStatus,
        next_follow_up_date: date || null,
        next_follow_up_time: time || null,
        last_contact_note: note || undefined,
        contacted_by: 'Berkay Mert',
      });
    } finally {
      setStatusChanging(false);
    }
  };

  const handleFollowUpModalSubmit = async () => {
    if (!pendingFollowUpStatus || !followUpDate) return;
    await executeStatusChange(pendingFollowUpStatus, followUpDate, followUpTime || null, statusNote);
    setShowFollowUpModal(false);
    setPendingFollowUpStatus(null);
  };

  const handleCallAction = async () => {
    if (!customer?.phone) return;
    await executeStatusChange('Arandı', null, null, '');
    window.open(`tel:${customer.phone}`, '_self');
  };

  const handlePostponeSubmit = () => {
    onPostpone(postponeDate, postponeAmount);
    setShowPostponeDialog(false);
  };

  const handleActivitySubmit = () => {
    if (!activityNote.trim()) return;
    onAddActivity(activityNote);
    setActivityNote('');
    setShowActivityDialog(false);
  };

  const pendingStatusDef = getFollowUpStatusDef(pendingFollowUpStatus || '');

  const isAnyDropdownOpen = showStatusDropdown || showMobileDropdown;

  // Portal dropdown content
  const dropdownContent = isAnyDropdownOpen && createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-background-50 border border-background-200 rounded-xl shadow-lg p-1.5 overflow-y-auto animate-fade-in"
      style={{
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        maxHeight: 320,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {FOLLOW_UP_STATUSES.map(s => (
        <button
          key={s.key}
          onClick={() => handleQuickStatusChange(s.key)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-background-100 ${followUpStatus === s.key ? 'bg-background-100' : ''}`}
        >
          <i className={`${s.icon} ${s.color}`}></i>
          <span className={s.color}>{s.label}</span>
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <>
      {dropdownContent}

      {/* Desktop Table Row */}
      <div className={`hidden lg:block bg-background-50 rounded-xl border ${isOverdue ? 'border-red-300 bg-red-50/30' : 'border-background-200/70'}`}>
        <div className="grid grid-cols-12 gap-2 p-4 items-center">
          {/* Customer Info */}
          <div className="col-span-2 min-w-0">
            <button onClick={() => customer && onNavigate(`/customers/${customer.id}`)} className="text-left w-full cursor-pointer">
              <p className="text-sm font-medium text-foreground-800 truncate hover:text-primary-500 transition-colors">
                {customer?.full_name || promise.customer_name || '—'}
              </p>
              <p className="text-xs text-foreground-400">{customer?.phone || promise.customer_phone || '—'}</p>
            </button>
          </div>

          {/* Current Customer Debt (LIVE) */}
          <div className="col-span-1">
            <p className="text-xs text-foreground-400">Güncel Borç</p>
            <p className={`text-sm font-bold ${customerDebt > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
              {formatTRY(customerDebt)}
            </p>
          </div>

          {/* Promised Amount */}
          <div className="col-span-1">
            <p className="text-xs text-foreground-400">Söz Verilen</p>
            <p className="text-sm font-semibold text-foreground-800">{formatTRY(promise.promised_amount || 0)}</p>
            {promise.paid_amount > 0 && (
              <p className="text-xs text-emerald-600">Ödenen: {formatTRY(promise.paid_amount)}</p>
            )}
          </div>

          {/* Debt Source */}
          <div className="col-span-1">
            <p className="text-xs text-foreground-400">Bağlı Borç</p>
            <p className="text-xs font-medium text-foreground-600">{reference || 'Genel Borç'}</p>
            {sourceRemaining != null && (
              <p className={`text-xs ${sourceRemaining <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                Kalan: {formatTRY(sourceRemaining)}
              </p>
            )}
          </div>

          {/* Follow-Up Status */}
          <div className="col-span-1">
            <button
              ref={desktopTriggerRef}
              onClick={handleDesktopToggle}
              disabled={isLoading || statusChanging}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${statusDef?.bg || 'bg-foreground-100'} ${statusDef?.color || 'text-foreground-500'} hover:opacity-80 disabled:opacity-50`}
            >
              {statusChanging ? (
                <><i className="ri-loader-4-line animate-spin"></i>...</>
              ) : (
                <>{statusDef?.label || followUpStatus} <i className="ri-arrow-down-s-line text-[10px]"></i></>
              )}
            </button>
          </div>

          {/* Promise Date + Follow-up */}
          <div className="col-span-1">
            <p className="text-xs text-foreground-400">Söz Tarihi</p>
            <p className="text-xs font-medium text-foreground-700">{originalDateDisplay}</p>
            {promise.next_follow_up_date && (
              <p className={`text-[10px] ${promise.next_follow_up_date < todayStr ? 'text-red-500' : 'text-foreground-400'}`}>
                Takip: {followUpDateDisplay}
              </p>
            )}
          </div>

          {/* Last Note */}
          <div className="col-span-1">
            <p className="text-xs text-foreground-400 truncate max-w-[100px]" title={promise.last_contact_note || promise.staff_note || ''}>
              {promise.last_contact_note || promise.staff_note || '—'}
            </p>
            <p className="text-[10px] text-foreground-300">{formattedLastContacted}</p>
          </div>

          {/* Actions */}
          <div className="col-span-3 flex items-center gap-1 justify-end">
            {!isCompleted && (
              <>
                <button onClick={handleCallAction} disabled={isLoading || statusChanging} className="w-8 h-8 rounded-lg bg-sky-50 hover:bg-sky-100 flex items-center justify-center cursor-pointer disabled:opacity-50" title="Ara">
                  <i className="ri-phone-line text-sky-600 text-sm"></i>
                </button>
                {customer?.phone && (
                  <a href={`https://wa.me/90${(customer.phone || '').replace(/\s/g, '').replace(/^0/, '')}`} target="_blank" rel="nofollow noopener noreferrer" className="w-8 h-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center cursor-pointer" title="WhatsApp">
                    <i className="ri-whatsapp-line text-emerald-600 text-sm"></i>
                  </a>
                )}
                <button onClick={onTakePayment} disabled={isLoading} className="w-8 h-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center cursor-pointer disabled:opacity-50" title="Ödeme Al">
                  <i className="ri-bank-card-line text-emerald-600 text-sm"></i>
                </button>
                <button onClick={() => { setPostponeDate(promise.promised_date); setPostponeAmount(promise.promised_amount); setShowPostponeDialog(true); }} disabled={isLoading} className="w-8 h-8 rounded-lg bg-amber-50 hover:bg-amber-100 flex items-center justify-center cursor-pointer disabled:opacity-50" title="Ertele">
                  <i className="ri-calendar-schedule-line text-amber-600 text-sm"></i>
                </button>
                <button onClick={() => { setActivityNote(''); setShowActivityDialog(true); }} disabled={isLoading} className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer disabled:opacity-50" title="Not Ekle">
                  <i className="ri-sticky-note-line text-foreground-500 text-sm"></i>
                </button>
              </>
            )}
            <button onClick={() => setShowHistoryPanel(true)} className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer" title="Geçmiş">
              <i className="ri-history-line text-foreground-500 text-sm"></i>
            </button>
            <button onClick={onView} className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer" title="Görüntüle">
              <i className="ri-eye-line text-foreground-500 text-sm"></i>
            </button>
            <button onClick={onDelete} disabled={isLoading} className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center cursor-pointer disabled:opacity-50" title="Sil">
              <i className="ri-delete-bin-line text-red-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Bottom info row */}
        <div className="flex items-center gap-3 px-4 pb-3 border-t border-background-100 pt-2">
          <span className="text-xs text-foreground-400">
            Son temas: {formattedLastContacted}
          </span>
          {promise.last_contact_note && (
            <span className="text-xs text-foreground-500 truncate max-w-[300px]">
              · {promise.last_contact_note}
            </span>
          )}
          {promise.doneBy && (
            <span className="text-xs text-foreground-400 ml-auto">
              {promise.contacted_by || promise.assigned_user || '—'}
            </span>
          )}
        </div>
      </div>

      {/* Mobile Card */}
      <div className={`lg:hidden bg-background-50 rounded-xl border ${isOverdue ? 'border-red-300 bg-red-50/30' : 'border-background-200/70'} p-3.5 space-y-3`}>
        {/* Top row: customer + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <button onClick={() => customer && onNavigate(`/customers/${customer.id}`)} className="text-left cursor-pointer">
              <p className="text-sm font-semibold text-foreground-800 truncate">{customer?.full_name || promise.customer_name || '—'}</p>
            </button>
            <p className="text-xs text-foreground-400 mt-0.5">{customer?.phone || promise.customer_phone || '—'}</p>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusDef?.bg || 'bg-foreground-100'} ${statusDef?.color || 'text-foreground-500'}`}>
            {statusDef?.label || followUpStatus}
          </span>
        </div>

        {/* Amounts + Debt row */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] text-foreground-400">Söz Verilen</p>
            <p className="text-sm font-bold text-foreground-800">{formatTRY(promise.promised_amount || 0)}</p>
            {promise.paid_amount > 0 && (
              <p className="text-xs text-emerald-600">Ödenen: {formatTRY(promise.paid_amount)}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] text-foreground-400">Güncel Borç</p>
            <p className={`text-sm font-bold ${customerDebt > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
              {formatTRY(customerDebt)}
            </p>
          </div>
        </div>

        {/* Source + Dates */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] text-foreground-400">Bağlı Borç</p>
            <p className="text-xs font-medium text-foreground-600">{reference || 'Genel Borç'}</p>
            {sourceRemaining != null && (
              <p className={`text-xs ${sourceRemaining <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                Kalan: {formatTRY(sourceRemaining)}
              </p>
            )}
          </div>
          <div>
            <p className="text-[10px] text-foreground-400">Söz / Takip</p>
            <p className="text-xs font-medium text-foreground-700">{originalDateDisplay}</p>
            {promise.next_follow_up_date && (
              <p className="text-xs text-foreground-400">
                Takip: {followUpDateDisplay} {promise.next_follow_up_time ? `· ${followUpTimeDisplay}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Last note */}
        {(promise.last_contact_note || promise.staff_note) && (
          <p className="text-xs text-foreground-400 truncate">{promise.last_contact_note || promise.staff_note}</p>
        )}

        {/* Contact info */}
        {promise.last_contacted_at && (
          <p className="text-[10px] text-foreground-300">Son temas: {formattedLastContacted}</p>
        )}

        {/* Status Change Dropdown - Mobile (no relative wrapper needed anymore) */}
        {!isCompleted && (
          <div>
            <button
              ref={mobileTriggerRef}
              onClick={handleMobileToggle}
              disabled={isLoading || statusChanging}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-xs font-medium text-foreground-600 hover:bg-background-200 cursor-pointer disabled:opacity-50 whitespace-nowrap"
            >
              {statusChanging ? <><i className="ri-loader-4-line animate-spin"></i>Değiştiriliyor...</> : <><i className="ri-arrow-left-right-line"></i>Durum Değiştir</>}
            </button>
          </div>
        )}

        {/* Action buttons row */}
        <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-background-100">
          <button onClick={handleCallAction} disabled={isLoading || statusChanging} className="flex items-center gap-1 text-xs bg-sky-50 text-sky-600 hover:bg-sky-100 px-2.5 py-1.5 rounded-lg cursor-pointer disabled:opacity-50 whitespace-nowrap">
            <i className="ri-phone-line"></i>Ara
          </button>
          {customer?.phone && (
            <a href={`https://wa.me/90${(customer.phone || '').replace(/\s/g, '').replace(/^0/, '')}`} target="_blank" rel="nofollow noopener noreferrer" className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-600 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line"></i>WhatsApp
            </a>
          )}
          {!isCompleted && (
            <>
              <button onClick={onTakePayment} disabled={isLoading} className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-600 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg cursor-pointer disabled:opacity-50 whitespace-nowrap">
                <i className="ri-bank-card-line"></i>Ödeme Al
              </button>
              <button onClick={() => { setPostponeDate(promise.promised_date); setPostponeAmount(promise.promised_amount); setShowPostponeDialog(true); }} disabled={isLoading} className="flex items-center gap-1 text-xs bg-amber-50 text-amber-600 hover:bg-amber-100 px-2.5 py-1.5 rounded-lg cursor-pointer disabled:opacity-50 whitespace-nowrap">
                <i className="ri-calendar-schedule-line"></i>Ertele
              </button>
            </>
          )}
          <button onClick={() => { setActivityNote(''); setShowActivityDialog(true); }} disabled={isLoading} className="flex items-center gap-1 text-xs bg-background-100 text-foreground-500 hover:bg-background-200 px-2.5 py-1.5 rounded-lg cursor-pointer disabled:opacity-50 whitespace-nowrap">
            <i className="ri-sticky-note-line"></i>Not
          </button>
          <button onClick={() => setShowHistoryPanel(true)} className="flex items-center gap-1 text-xs bg-background-100 text-foreground-500 hover:bg-background-200 px-2.5 py-1.5 rounded-lg cursor-pointer whitespace-nowrap">
            <i className="ri-history-line"></i>Geçmiş
          </button>
          <button onClick={onView} className="flex items-center gap-1 text-xs bg-background-100 text-foreground-500 hover:bg-background-200 px-2.5 py-1.5 rounded-lg cursor-pointer whitespace-nowrap">
            <i className="ri-eye-line"></i>Detay
          </button>
          <button onClick={onDelete} disabled={isLoading} className="flex items-center gap-1 text-xs bg-red-50 text-red-500 hover:bg-red-100 px-2.5 py-1.5 rounded-lg cursor-pointer disabled:opacity-50 whitespace-nowrap">
            <i className="ri-delete-bin-line"></i>Sil
          </button>
        </div>
      </div>

      {/* Follow-Up Status Change Modal (for statuses requiring date/time) */}
      {showFollowUpModal && pendingFollowUpStatus && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[80] animate-fade-in" onClick={() => { setShowFollowUpModal(false); setPendingFollowUpStatus(null); }} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[81] max-w-sm mx-auto bg-background-50 rounded-2xl p-5 shadow-xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${pendingStatusDef?.bg || 'bg-foreground-100'}`}>
                <i className={`${pendingStatusDef?.icon || 'ri-question-line'} ${pendingStatusDef?.color || 'text-foreground-500'}`}></i>
              </span>
              <h3 className="text-base font-semibold text-foreground-900">{pendingStatusDef?.label || pendingFollowUpStatus}</h3>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-foreground-500 block mb-1">Yeni Takip Tarihi <span className="text-red-400">*</span></label>
                <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800" />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground-500 block mb-1">Takip Saati</label>
                <input type="time" value={followUpTime} onChange={e => setFollowUpTime(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800" />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground-500 block mb-1">Kısa Not</label>
                <textarea
                  value={statusNote}
                  onChange={e => setStatusNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800 resize-none"
                  placeholder="Müşteriyle görüşme notu..."
                />
                <p className="text-xs text-foreground-300 mt-0.5">{statusNote.length}/500</p>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowFollowUpModal(false); setPendingFollowUpStatus(null); }} className="flex-1 btn-outline cursor-pointer text-sm whitespace-nowrap">İptal</button>
              <button
                onClick={handleFollowUpModalSubmit}
                disabled={!followUpDate || statusChanging}
                className="flex-1 btn-primary cursor-pointer text-sm whitespace-nowrap disabled:opacity-50"
              >
                {statusChanging ? 'Kaydediliyor...' : 'Durumu Güncelle'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Postpone Dialog */}
      {showPostponeDialog && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[80] animate-fade-in" onClick={() => setShowPostponeDialog(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[81] max-w-sm mx-auto bg-background-50 rounded-2xl p-5 shadow-xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground-900 mb-4">Ödeme Sözünü Ertele</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-foreground-500 block mb-1">Yeni Söz Tarihi</label>
                <input type="date" value={postponeDate} onChange={e => setPostponeDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800" />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground-500 block mb-1">Yeni Tutar (TL)</label>
                <CurrencyInput value={String(postponeAmount)} onChange={v => setPostponeAmount(Number(v) || 0)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowPostponeDialog(false)} className="flex-1 btn-outline cursor-pointer text-sm whitespace-nowrap">İptal</button>
              <button onClick={handlePostponeSubmit} className="flex-1 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap">Ertele</button>
            </div>
          </div>
        </>
      )}

      {/* Add Activity Dialog */}
      {showActivityDialog && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[80] animate-fade-in" onClick={() => setShowActivityDialog(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[81] max-w-sm mx-auto bg-background-50 rounded-2xl p-5 shadow-xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground-900 mb-4">Not Ekle</h3>
            <div>
              <label className="text-xs font-medium text-foreground-500 block mb-1">Not</label>
              <textarea value={activityNote} onChange={e => setActivityNote(e.target.value)} rows={3} maxLength={500} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800 resize-none" placeholder="Görüşme detaylarını yazın..." />
              <p className="text-xs text-foreground-300 mt-1">{activityNote.length}/500</p>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowActivityDialog(false)} className="flex-1 btn-outline cursor-pointer text-sm whitespace-nowrap">İptal</button>
              <button onClick={handleActivitySubmit} disabled={!activityNote.trim()} className="flex-1 btn-primary cursor-pointer text-sm whitespace-nowrap disabled:opacity-50">Kaydet</button>
            </div>
          </div>
        </>
      )}

      {/* History Panel */}
      {showHistoryPanel && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[80] animate-fade-in" onClick={() => setShowHistoryPanel(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[81] max-w-md mx-auto bg-background-50 rounded-2xl p-5 shadow-xl animate-slide-up max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground-900">Aktivite Geçmişi</h3>
              <button onClick={() => setShowHistoryPanel(false)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            {activities.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-6">Henüz aktivite kaydı yok.</p>
            ) : (
              <div className="space-y-1">
                {activities.map((act: any, idx: number) => {
                  const actDef = getFollowUpStatusDef(act.activity_type);
                  return (
                    <div key={act.id || idx} className="flex gap-3 py-2.5 border-b border-background-100 last:border-0">
                      <div className="flex flex-col items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${actDef?.bg || 'bg-foreground-100'}`}>
                          <i className={`${actDef?.icon || 'ri-record-circle-line'} ${actDef?.color || 'text-foreground-400'} text-xs`}></i>
                        </div>
                        {idx < activities.length - 1 && <div className="w-px flex-1 bg-background-200 my-0.5"></div>}
                      </div>
                      <div className="flex-1 min-w-0 pb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-medium ${actDef?.color || 'text-foreground-500'}`}>{actDef?.label || act.activity_type}</span>
                          <span className="text-[10px] text-foreground-300">
                            {act.created_at ? new Date(act.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                        </div>
                        {act.old_status && act.new_status && (
                          <p className="text-xs text-foreground-400 mt-0.5">
                            {act.old_status} → {act.new_status}
                          </p>
                        )}
                        {act.old_follow_up_date && act.new_follow_up_date && (
                          <p className="text-xs text-foreground-400 mt-0.5">
                            Takip: {new Date(act.old_follow_up_date).toLocaleDateString('tr-TR')} → {new Date(act.new_follow_up_date).toLocaleDateString('tr-TR')}
                            {act.new_follow_up_time ? ` ${act.new_follow_up_time}` : ''}
                          </p>
                        )}
                        {act.note && <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">{act.note}</p>}
                        {act.created_by && <p className="text-[10px] text-foreground-300 mt-0.5">— {act.created_by}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}